import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { FormattedText, FeedbackReport } from "@/lib/tcf";
import {
  Mic, PenLine, Search, ArrowLeft, ArrowRight, CheckCircle2, Wrench, TrendingUp, BookOpenCheck,
  PenSquare, Loader2, Sparkles, Crown,
} from "lucide-react";

const SKILLS = [
  { k: "", label: "All" },
  { k: "speaking", label: "Speaking" },
  { k: "writing", label: "Writing" },
];

function LevelColumn({ level, data }) {
  const isB2 = level === "B2";
  return (
    <div className={`rounded-2xl border p-5 h-full ${isB2 ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`} data-testid={`sample-level-${level}`}>
      <div className="flex items-center justify-between mb-3">
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${isB2 ? "emerald-surface text-white" : "bg-secondary"}`}>{level} · CLB {data.clb}</span>
      </div>
      <p className="text-sm leading-relaxed whitespace-pre-wrap"><FormattedText text={data.text} /></p>

      <div className="mt-4 rounded-xl bg-background border border-border p-3">
        <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">Why this is {level}</p>
        <p className="text-sm text-muted-foreground">{data.explanation}</p>
      </div>

      {data.strengths?.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold flex items-center gap-1.5 mb-1"><CheckCircle2 className="h-3.5 w-3.5 text-primary" /> Strengths</p>
          <ul className="space-y-1 text-sm text-muted-foreground">{data.strengths.map((s, i) => <li key={`s-${i}`}>• {s}</li>)}</ul>
        </div>
      )}
      {data.improve?.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold flex items-center gap-1.5 mb-1"><Wrench className="h-3.5 w-3.5 text-primary" /> Areas to improve</p>
          <ul className="space-y-1 text-sm text-muted-foreground">{data.improve.map((s, i) => <li key={`i-${i}`}>• {s}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function GapBar({ b1, b2, you }) {
  const fill = you != null ? Math.max(6, Math.min(100, (you / (b2 || 7)) * 100)) : 6;
  const reached = b2 != null && you != null && you >= b2;
  return (
    <div className="rounded-2xl border border-border bg-background p-5" data-testid="gap-bar">
      <div className="flex items-center justify-between text-sm font-semibold">
        <span className="text-muted-foreground">B1 · CLB {b1 ?? "–"}</span>
        <span className={reached ? "text-primary" : ""}>You · around CLB {you ?? "–"}</span>
        <span className="text-primary">B2 · CLB {b2 ?? "–"}</span>
      </div>
      <div className="mt-3 h-2.5 rounded-full bg-secondary overflow-hidden">
        <div className="h-full emerald-surface rounded-full transition-all duration-700" style={{ width: `${fill}%` }} />
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        {reached
          ? "🎉 You've reached the B2 model's band — polish it and keep it consistent!"
          : `You're ${Math.max(0, (b2 || 0) - you)} point(s) below the B2 model. The tips below close that gap.`}
      </p>
    </div>
  );
}

function HighlightedModel({ text, markers }) {
  if (!markers?.length) return <FormattedText text={text} />;
  const esc = markers.map((m) => m.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${esc.join("|")})`, "gi");
  const set = new Set(markers.map((m) => m.toLowerCase()));
  const parts = String(text).split(re);
  return (
    <span>{parts.map((p, i) => (set.has(p.toLowerCase())
      ? <mark key={i} className="bg-primary/20 text-primary font-semibold rounded px-1">{p}</mark>
      : <span key={i}>{p}</span>))}</span>
  );
}

function TryItPanel({ sample }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [attempt, setAttempt] = useState(null);
  const [compare, setCompare] = useState(false);
  const [gated, setGated] = useState(false);
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;

  const submit = async () => {
    if (words < 10) { toast.error("Write a few sentences first."); return; }
    setLoading(true); setGated(false);
    try {
      const { data } = await api.post(`/samples/${sample.id}/attempt`, { text });
      setAttempt(data); setCompare(false);
      setTimeout(() => document.getElementById("try-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    } catch (e) {
      if (e.response?.status === 402) { setGated(true); toast.error("Free trial ended — go Premium to keep scoring your answers."); }
      else toast.error(e.response?.data?.detail || "Could not evaluate your answer.");
    } finally { setLoading(false); }
  };

  return (
    <div className="mt-6 rounded-2xl border-2 border-primary/30 bg-primary/5 p-6" data-testid="try-it-panel">
      <h3 className="font-semibold flex items-center gap-2"><PenSquare className="h-5 w-5 text-primary" /> Try it yourself</h3>
      <p className="text-sm text-muted-foreground mt-1">Write your own answer to this exact prompt — get a tentative level estimate and see your answer rewritten at CLB5 and CLB7.</p>

      <textarea data-testid="try-input" value={text} onChange={(e) => setText(e.target.value)} rows={6}
        placeholder="Écrivez votre réponse en français…"
        className="mt-4 w-full p-4 rounded-xl bg-background border border-border focus:outline-none focus:ring-2 focus:ring-ring resize-y leading-relaxed" />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{words} words</span>
        <button onClick={submit} disabled={loading || words < 10} data-testid="try-submit"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Evaluating…</> : <>Get my feedback <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>

      {gated && (
        <div className="mt-4 rounded-xl bg-card border border-border p-4 flex items-center justify-between gap-3" data-testid="try-gated">
          <div className="flex items-center gap-2 text-sm"><Crown className="h-5 w-5 text-primary" /> Your free trial has ended.</div>
          <Link to="/premium" className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold">Go Premium</Link>
        </div>
      )}

      {attempt && (
        <div id="try-result" className="mt-6 space-y-5" data-testid="try-result">
          <GapBar b1={attempt.b1_clb} b2={attempt.b2_clb} you={attempt.result?.clb} />
          <FeedbackReport result={attempt.result} />

          <button onClick={() => setCompare((c) => !c)} data-testid="compare-b2-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-secondary hover:bg-accent text-sm font-semibold transition-colors">
            <Sparkles className="h-4 w-4 text-primary" /> {compare ? "Hide B2 comparison" : "See how I compare to the B2 model"}
          </button>

          {compare && (
            <div className="rounded-2xl border border-border bg-background p-5" data-testid="b2-comparison">
              {attempt.b2_markers_missing?.length > 0 ? (
                <>
                  <p className="text-sm font-semibold">Structures the B2 model uses that yours doesn't:</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {attempt.b2_markers_missing.map((m, i) => (
                      <span key={`mk-${i}`} className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">{m}</span>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-primary font-semibold">Nice — you already use the key B2 connectors from the model!</p>
              )}
              <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mt-4 mb-1">B2 model answer (missing structures highlighted)</p>
              <p className="text-sm leading-relaxed whitespace-pre-wrap"><HighlightedModel text={attempt.b2_model} markers={attempt.b2_markers_missing} /></p>
              {attempt.jump_tips?.length > 0 && (
                <div className="mt-4">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">How to level up</p>
                  <ul className="space-y-1.5">
                    {attempt.jump_tips.map((tp, i) => (
                      <li key={`tj-${i}`} className="flex gap-2 text-sm"><TrendingUp className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span><FormattedText text={tp} /></span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SampleDetail({ sample, onBack }) {
  const SkillIcon = sample.skill === "speaking" ? Mic : PenLine;
  return (
    <div data-testid="sample-detail">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="h-4 w-4" /> Back to library</button>

      <div className="rounded-2xl bg-card border border-border p-6">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full emerald-surface text-white"><SkillIcon className="h-3.5 w-3.5" /> {sample.skill === "speaking" ? "Speaking" : "Writing"}</span>
          <span className="px-2.5 py-1 rounded-full bg-secondary">{sample.task}</span>
        </div>
        <h1 className="font-display text-2xl font-semibold mt-4">{sample.prompt}</h1>
        {sample.prompt_en && <p className="text-muted-foreground mt-1 text-sm italic">{sample.prompt_en}</p>}
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-5">
        {sample.levels?.B1 && <LevelColumn level="B1" data={sample.levels.B1} />}
        {sample.levels?.B2 && <LevelColumn level="B2" data={sample.levels.B2} />}
      </div>

      {sample.skill === "writing" && <TryItPanel sample={sample} />}

      {sample.jump_tips?.length > 0 && (
        <div className="mt-5 rounded-2xl emerald-surface grain relative overflow-hidden p-6 text-white" data-testid="sample-jump-tips">
          <h3 className="font-semibold flex items-center gap-2"><TrendingUp className="h-5 w-5" /> How to jump from B1 → B2 (same prompt)</h3>
          <ul className="mt-3 space-y-2">
            {sample.jump_tips.map((tip, i) => (
              <li key={`jt-${i}`} className="flex items-start gap-2 text-sm text-white/95">
                <span className="shrink-0 mt-0.5 h-5 w-5 rounded-full bg-white/15 grid place-items-center text-[11px] font-bold">{i + 1}</span>
                <FormattedText text={tip} strongClassName="text-amber-200" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Samples() {
  const [skill, setSkill] = useState("");
  const [q, setQ] = useState("");
  const [samples, setSamples] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (skill) params.skill = skill;
    if (q.trim()) params.q = q.trim();
    api.get("/samples", { params }).then((r) => setSamples(r.data)).catch((e) => console.error("samples", e)).finally(() => setLoading(false));
  }, [skill, q]);
  useEffect(() => { load(); }, [load]);

  if (active) return <SampleDetail sample={active} onBack={() => setActive(null)} />;

  return (
    <div data-testid="samples-page">
      <h1 className="font-display text-4xl font-semibold">Sample Answers</h1>
      <p className="text-muted-foreground mt-2">See the SAME prompt answered at B1 and B2, with an English explanation of the level and exactly how to level up.</p>
      <div className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground"><BookOpenCheck className="h-3.5 w-3.5 text-primary" /> Curated model answers · calibrated to TCF Canada.</div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {SKILLS.map((s) => (
            <button key={s.k} onClick={() => setSkill(s.k)} data-testid={`samples-filter-${s.k || "all"}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${skill === s.k ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-accent"}`}>{s.label}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search topics…" data-testid="samples-search"
            className="w-full h-10 pl-9 pr-3 rounded-full bg-card border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
      </div>

      {loading ? (
        <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
      ) : samples.length === 0 ? (
        <p className="mt-8 text-sm text-muted-foreground">No samples match your search.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {samples.map((s, i) => {
            const SkillIcon = s.skill === "speaking" ? Mic : PenLine;
            return (
              <motion.button key={s.id} onClick={() => setActive(s)} data-testid={`sample-card-${s.id}`}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="text-left rounded-2xl bg-card border border-border p-5 hover:shadow-lg hover:-translate-y-1 transition-all flex flex-col">
                <div className="flex items-center gap-2 text-xs font-semibold">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-accent text-accent-foreground"><SkillIcon className="h-3 w-3" /> {s.skill === "speaking" ? "Speaking" : "Writing"}</span>
                  <span className="text-muted-foreground">{s.task}</span>
                </div>
                <p className="mt-3 text-sm font-medium leading-snug line-clamp-3 flex-1">{s.prompt}</p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex gap-1.5">
                    <span className="px-2 py-0.5 rounded-full bg-secondary text-xs font-bold">B1</span>
                    <span className="px-2 py-0.5 rounded-full emerald-surface text-white text-xs font-bold">B2</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary">View <ArrowRight className="h-4 w-4" /></span>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
}
